/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on "What service do you need?" to open the dropdown of all the services available
 * [Event Spec M2 Intuitive mWeb Homepage Form](https://roverdotcom.atlassian.net/wiki/spaces/BKS/pages/3612672002/Event+Spec+M2+Intuitive+mWeb+Homepage+Form)
 *
 * &nbsp;
 */
export class MobileWebHomePageServiceTypeDropdownClick extends AnalyticsEvent {
  eventName = 'mobileweb-homepage-servicetype-dropdown-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: { serviceType?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user changes the service selected on the simplified mobile owner search.
 * [Event Spec M2 Intuitive mWeb Homepage Form](https://roverdotcom.atlassian.net/wiki/spaces/BKS/pages/3612672002/Event+Spec+M2+Intuitive+mWeb+Homepage+Form)
 *
 * &nbsp;
 */
export class MobileWebHomePageServiceTypeChange extends AnalyticsEvent {
  eventName = 'mobileweb-homepage-servicetype-change';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: { serviceType?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user searches for a daycare service without selecting a service type while in the DaycareRequired experiment.
 * [Event Spec for Gingr Pilot](https://roverdotcom.atlassian.net/wiki/spaces/adjacencies/pages/3498871601/Event+Specs+Full+Story+for+Gingr+Pilot#Required-Daycare-Filter-Error-(Homepage-and-Search))
 *
 * &nbsp;
 */
export class SearchPageDaycareTypeRequiredError extends AnalyticsEvent {
  eventName = 'daycare-type-required-error';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.bookings.external_platform;
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on one of the daycare type options in the Doggy Day Care filter.
 * [Event Spec for Gingr Pilot](https://roverdotcom.atlassian.net/wiki/spaces/adjacencies/pages/3498871601/Event+Specs+Full+Story+for+Gingr+Pilot#Select-Day-Care-Type-Filter-(Homepage-and-Search))
 *
 * &nbsp;
 */
export class SearchPageDaycareTypeFilterSelect extends AnalyticsEvent {
  eventName = 'daycare-type-filter-select';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.bookings.external_platform;

  constructor(eventProperties: { daycareType?: string; placement?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when `NoFacilitiesCallout` is displayed on the search page.
 * Read more: https://roverdotcom.atlassian.net/wiki/spaces/adjacencies/pages/3498871601/Event+Specs+Full+Story+for+Gingr+Pilot#No-Facility-Results-Test-Messaging
 *
 * &nbsp;
 */
export class SearchPageDaycareNoResults extends AnalyticsEvent {
  eventName = 'search-required-test-no-results';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.bookings.external_platform;
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user searches for a boarding service without selecting a daycare type.
 * [Event Spec for Gingr Pilot](https://roverdotcom.atlassian.net/wiki/spaces/adjacencies/pages/3498871601/Event+Specs+Full+Story+for+Gingr+Pilot#Required-Boarding-Filter-Error-(Homepage-and-Search))
 *
 * &nbsp;
 */
export class SearchPageBoardingTypeRequiredError extends AnalyticsEvent {
  eventName = 'boarding-type-required-error';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.bookings.external_platform;
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on one of the daycare type options in the Boarding filter.
 * [Event Spec for Gingr Pilot](https://roverdotcom.atlassian.net/wiki/spaces/adjacencies/pages/3498871601/Event+Specs+Full+Story+for+Gingr+Pilot#Select-Boarding-Type-Filter-(Homepage-and-Search))
 *
 * &nbsp;
 */
export class SearchPageBoardingTypeFilterSelect extends AnalyticsEvent {
  eventName = 'boarding-type-filter-select';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.bookings.external_platform;

  constructor(eventProperties: { boardingType?: string; placement?: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a user clicks "Show me day care centers" CTA on daycare homepage banner.
 * Read more: https://roverdotcom.atlassian.net/wiki/spaces/adjacencies/pages/3498871601/Event+Specs+Full+Story+for+Gingr+Pilot#Desktop-Homepage-Daycare-Banner-Click
 *
 * &nbsp;
 */
export class HomepageDaycareBannerClick extends AnalyticsEvent {
  eventName = 'homepage-daycare-banner-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.bookings.external_platform;
}
